import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Upload, FileText, X, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Normalize a stored file-field value → {document_id, filename} or null.
export const normalizeFileValue = (v) => {
  if (!v) return null;
  if (typeof v === 'object' && (v.document_id || v.filename)) return v;
  if (typeof v === 'string') {
    try {
      const p = JSON.parse(v);
      if (p && (p.document_id || p.filename)) return p;
    } catch { /* plain string — not a file value */ }
  }
  return null;
};

/**
 * Shared "file" form-field widget (staff note editor + client portal).
 * Caller renders its own label and supplies `uploadFile(file) → {document_id, filename}`.
 */
export const FormFileUploadField = ({
  value,
  disabled = false,
  uploadFile,
  onChange,
  onView,
  accept = 'image/*,.pdf,.heic',
  maxSizeMB = 25,
  testId,
}) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const info = normalizeFileValue(value);

  const handleSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`File exceeds the ${maxSizeMB}MB limit`);
      return;
    }
    setUploading(true);
    try {
      const uploaded = await uploadFile(file);
      onChange?.(uploaded);
      toast.success(`${file.name} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed — please try again');
    } finally {
      setUploading(false);
    }
  };

  if (disabled) {
    return info ? (
      <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border text-sm" data-testid={testId ? `${testId}-readonly` : undefined}>
        <FileText className="h-4 w-4 text-teal-600 shrink-0" />
        <span className="truncate min-w-0">{info.filename || 'Uploaded file'}</span>
        {onView && info.document_id && (
          <button type="button" onClick={() => onView(info)} className="ml-auto text-teal-700 hover:text-teal-900 shrink-0" title="View file">
            <Eye className="h-4 w-4" />
          </button>
        )}
      </div>
    ) : (
      <div className="p-2 bg-gray-50 rounded border text-sm text-gray-400 italic" data-testid={testId ? `${testId}-empty` : undefined}>
        No file uploaded
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleSelect}
        data-testid={testId ? `${testId}-input` : undefined}
      />
      {info ? (
        <div className="flex items-center gap-2 p-2 bg-teal-50 rounded border border-teal-200 text-sm">
          <FileText className="h-4 w-4 text-teal-600 shrink-0" />
          <span className="truncate min-w-0 text-teal-900">{info.filename || 'Uploaded file'}</span>
          <div className="ml-auto flex items-center gap-1 shrink-0">
            {onView && info.document_id && (
              <button type="button" onClick={() => onView(info)} className="p-1 text-teal-700 hover:text-teal-900" title="View file">
                <Eye className="h-4 w-4" />
              </button>
            )}
            <Button type="button" size="sm" variant="outline" className="h-7 text-xs" disabled={uploading}
              onClick={() => inputRef.current?.click()} data-testid={testId ? `${testId}-replace-btn` : undefined}>
              {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Replace'}
            </Button>
            <button type="button" onClick={() => onChange?.('')} className="p-1 text-gray-400 hover:text-red-600" title="Remove"
              data-testid={testId ? `${testId}-remove-btn` : undefined}>
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        <Button type="button" variant="outline" disabled={uploading} onClick={() => inputRef.current?.click()}
          className="w-full justify-center gap-2 border-dashed" data-testid={testId ? `${testId}-upload-btn` : undefined}>
          {uploading ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</>
          ) : (
            <><Upload className="h-4 w-4" /> Upload file (image or PDF)</>
          )}
        </Button>
      )}
    </div>
  );
};

export default FormFileUploadField;
